import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {Select} from '@wandb/weave/components/Form/Select';
import React, {useMemo, useState} from 'react';
import {type MultiValue} from 'react-select';

import {useIsNgwEnabledInWorkspace} from '../../../../MetricSearch/useIsNgwEnabledInWorkspace';
import {useMediaKeys} from '../../../../PanelMediaBrowser/hooks/useMediaKeys';
import {useUpdateConfigWithMediaKeys} from '../../../../PanelMediaBrowser/hooks/useUpdateConfigWithMediaKeys';
import {SUPPORTED_MEDIA_DRAWER_TYPES} from '../../../../PanelMediaBrowser/supportedMediaDrawerTypes';
import {isValidMediaKeyOption} from '../../../../PanelMediaBrowser/utils/isValidMediaKeyOption';
import {
  usePanelMediaKeys,
  useShouldShowMediaKeysResetAtLevel,
} from '../../hooks';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';
import {MediaKeyMultiValue, type MediaKeyOption} from './mediaKeySelectShared';
import {MediaKeysMetricSearch} from './MediaKeysMetricSearch';

export const MediaKeys = () => {
  const {settingsLevel, panelRef} = useMediaSettingsContext();
  const isMediaKeysSet = useShouldShowMediaKeysResetAtLevel(
    settingsLevel,
    panelRef
  );
  // The two pickers are separate components so neither calls the other's hooks:
  // the metric-search one reads WorkspaceRefsContext, the legacy one reads key
  // info that NGW leaves empty.
  const isMetricSearchEnabled = useIsNgwEnabledInWorkspace();

  return (
    <div className="flex flex-col">
      <SettingTitle
        title="Media key(s)"
        tooltip="Keys for media per step. Log all keys for the same step in a single log() call."
        configField="mediaKeys"
        isDirty={isMediaKeysSet}
      />
      {isMetricSearchEnabled ? (
        <MediaKeysMetricSearch />
      ) : (
        <MediaKeysFromKeyInfo />
      )}
    </div>
  );
};

const MediaKeysFromKeyInfo = () => {
  const {config, updateConfig, panelRef} = useMediaSettingsContext();
  const mediaKeys = usePanelMediaKeys(panelRef);
  const updateConfigWithMediaKeys = useUpdateConfigWithMediaKeys();
  // Local state to control the dropdown so we don't have to wait for the updateConfig call to propagate
  const [keys, setKeys] = useState(mediaKeys ?? []);

  const {allMediaKeys, keyTypes} = useMediaKeys();
  const mediaKeysOptions = useMemo(
    () =>
      allMediaKeys
        .filter(key =>
          isValidMediaKeyOption(
            key,
            keyTypes,
            keys,
            SUPPORTED_MEDIA_DRAWER_TYPES
          )
        )
        .map(k => ({value: k, displayText: k})),
    [allMediaKeys, keyTypes, keys]
  );
  const selectedOptions = useMemo(
    () => mediaKeysOptions.filter(k => keys.includes(k.value)),
    [mediaKeysOptions, keys]
  );

  const onChangeMediaKeys = (value: MultiValue<MediaKeyOption>) => {
    const newValues = value.map(v => v.value).filter(isNotNullOrUndefined);

    setKeys(newValues);
    updateConfigWithMediaKeys({mediaKeys: newValues}, config, updateConfig);
  };

  return (
    <Select<MediaKeyOption, true>
      aria-label="Select Media Key"
      size="variable"
      value={selectedOptions}
      options={mediaKeysOptions}
      placeholder="Select Media Key"
      isMulti
      isSearchable
      closeMenuOnSelect={false}
      onChange={onChangeMediaKeys}
      getOptionLabel={opt => opt.displayText}
      components={{
        MultiValue: MediaKeyMultiValue,
      }}
    />
  );
};
